import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, AlertCircle, X } from 'lucide-react';

const PasswordResetModal = ({ isOpen, onClose, user, isSuccess, isLoading, error }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 transition-opacity z-[9998]"
            aria-hidden="true"
          >
            <div className="absolute inset-0 bg-gray-900 opacity-75"></div>
          </motion.div>

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative z-[9999] inline-block align-bottom bg-gray-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full border border-gray-700"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-iranian-600 to-iranian-700 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center">
                {isSuccess ? (
                  <CheckCircle className="h-6 w-6 text-green-300 mr-3" />
                ) : isLoading ? (
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                ) : (
                  <AlertCircle className="h-6 w-6 text-yellow-300 mr-3" />
                )}
                <h3 className="text-lg font-semibold text-white">
                  {isLoading ? 'Réinitialisation en cours...' : 
                   isSuccess ? 'Mot de passe réinitialisé !' : 
                   'Confirmation de réinitialisation'}
                </h3>
              </div>
              <button
                onClick={() => {
                  console.log('Close button clicked');
                  onClose();
                }}
                className="text-gray-300 hover:text-white transition-colors cursor-pointer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-6">
              {isLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-iranian-500 mx-auto mb-4"></div>
                  <p className="text-gray-300 text-lg">
                    Réinitialisation du mot de passe pour <span className="text-white font-semibold">{user?.name}</span>
                  </p>
                  <p className="text-gray-400 text-sm mt-2">Veuillez patienter...</p>
                </div>
              ) : isSuccess ? (
                <div className="text-center py-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="h-10 w-10 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-3">
                    Mot de passe réinitialisé avec succès !
                  </h4>
                  <div className="bg-gray-700 rounded-lg p-4 mb-6 border border-gray-600">
                    <p className="text-gray-300 text-sm mb-2">
                      <strong>Utilisateur:</strong> {user?.name}
                    </p>
                    <p className="text-gray-300 text-sm mb-2">
                      <strong>Email:</strong> {user?.email}
                    </p>
                    <p className="text-gray-300 text-sm">
                      <strong>Nouveau mot de passe:</strong> 
                      <span className="text-green-400 font-mono ml-2">123456</span>
                    </p>
                  </div>
                  <p className="text-gray-400 text-sm">
                    Le mot de passe a été mis à jour dans la base de données.
                  </p>
                </div>
              ) : error ? (
                <div className="text-center py-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <AlertCircle className="h-10 w-10 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-3">
                    Erreur lors de la réinitialisation
                  </h4>
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6">
                    <p className="text-red-400 text-sm">
                      {error}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <AlertCircle className="h-10 w-10 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-3">
                    Confirmer la réinitialisation
                  </h4>
                  <p className="text-gray-300 mb-6">
                    Êtes-vous sûr de vouloir réinitialiser le mot de passe de{' '}
                    <span className="text-white font-semibold">{user?.name}</span> ?
                  </p>
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
                    <p className="text-yellow-400 text-sm">
                      <strong>Attention:</strong> Le nouveau mot de passe sera <span className="font-mono">123456</span>
                    </p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end space-x-3">
                {!isLoading && (
                                  <button
                  onClick={() => {
                    console.log('Cancel/Close button clicked');
                    onClose();
                  }}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors cursor-pointer ${
                    isSuccess 
                      ? 'bg-green-600 hover:bg-green-700 text-white' 
                      : 'bg-gray-600 hover:bg-gray-700 text-white'
                  }`}
                >
                  {isSuccess ? 'Fermer' : 'Annuler'}
                </button>
                )}
                                  {!isSuccess && !isLoading && !error && (
                    <button
                      onClick={() => {
                        console.log('Confirm button clicked');
                        onClose('confirm');
                      }}
                      className="px-4 py-2 bg-iranian-600 hover:bg-iranian-700 text-white rounded-lg font-medium transition-colors cursor-pointer"
                    >
                      Confirmer
                    </button>
                  )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

export default PasswordResetModal;
